// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Tab } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseTabList, BaseToolbar } from '~/components/common';
import {
  ActivityJobTab,
  CheckListJobTab,
  IncidentJobTab,
  InfoJobTab,
  TaskJobTab,
  JobCostingTab,
  NoteJobTab,
  JobFormModal,
} from '~/components/module/job';
// Others
import { JobTabEnum, RoleEnum } from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
import { EMPTY_STRING } from '~/utils/constants/common';
import { IBaseTabItem } from '~/utils/interface/common';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './JobDetail.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const JobDetail = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/jobs', 'common/base']);
  const { hash, pathname, search } = useLocation();
  const navigate = useNavigate();
  const { id: jobId } = useParams();
  const [searchParams] = useSearchParams();
  const currentRole: string = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0] ?? EMPTY_STRING;

  const getMenuItems = useCallback(() => {
    const items: Array<IBaseTabItem> = [{ label: JobTabEnum.INFORMATION, component: <InfoJobTab /> }];
    items.push({ label: JobTabEnum.TASKS, component: <TaskJobTab /> });
    items.push({ label: JobTabEnum.CHECKLIST, component: <CheckListJobTab /> });
    items.push({ label: JobTabEnum.INCIDENTS, component: <IncidentJobTab /> });
    items.push({ label: JobTabEnum.ACTIVITY, component: <ActivityJobTab /> });
    items.push({ label: JobTabEnum.COSTING, component: <JobCostingTab /> });
    items.push({ label: JobTabEnum.NOTES, component: <NoteJobTab /> });
    setTabItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [tabKey, setTabKey] = useState<string>(JobTabEnum.INFORMATION);
  const [tabItems, setTabItems] = useState<IBaseTabItem[]>([]);
  const [isOpenJobFormModal, setIsOpenJobFormModal] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (hash) setTabKey(hash.slice(1));
  }, [hash]);

  useEffect(() => {
    getMenuItems();
  }, [getMenuItems]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    navigate({
      pathname,
      search,
      hash: `#${newValue}`,
    });
  };

  const renderTabContent = () => {
    const item = tabItems.find((tabItem: IBaseTabItem) => {
      return tabKey === tabItem.label;
    });

    return item?.component;
  };

  const handleOnBack = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.jobs);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.jobs);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.jobs);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.jobs);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.jobs);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.jobs);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.jobs);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.jobs);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.jobs);
        break;
    }
  };

  const handleEdit = () => {
    handleShowJobFormModal();
  };

  const handleShowJobFormModal = () => {
    setIsOpenJobFormModal((prev) => !prev);
  };
  //#endregion Handle Function

  return (
    <Box id='jobDetailPage' className={cx('container')}>
      <BaseToolbar title={t('job_detail_title')} isBack onBackToPreviousPage={handleOnBack}>
        <Button
          variant='contained'
          onClick={handleEdit}
          startIcon={<img src={icons.commonIconPen} alt={t('common_alt_img')} />}
        >
          {t('table_action_edit', { ns: 'common/base' })}
        </Button>
      </BaseToolbar>

      <Stack height={5} />

      <BaseTabList value={tabKey} onChange={handleTabChange} type='small'>
        {tabItems.map((item, index) => (
          <Tab
            value={item.label}
            key={index}
            label={t(`tab_label_${item.label}`)}
            className={cx('tabItem', { active: tabKey === item.label })}
          />
        ))}
      </BaseTabList>

      <Box component={'div'} className={cx('body')}>
        <Box component={'div'} className={cx('tabContent')}>
          {renderTabContent()}
        </Box>
      </Box>

      {isOpenJobFormModal && <JobFormModal onClose={handleShowJobFormModal} idJob={jobId} />}
    </Box>
  );
};

export default JobDetail;
